import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { BlockDef } from "./block.interfaces";
import {
    createBlock,
    textField,
    descriptiveImageField,
    headingField,
    htmlField,
} from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    ThreeColumn,
    TwoRowBentoGrid,
    type ThreeColumnProps,
    type TwoRowBentoGridProps,
} from "@/views/blocks/bento/bento";
import { getStockPhotoUrl } from "@/apps/page/services/page.service";
import { useFieldConfig } from "./useFieldConfig";

export function useBentos() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const bentoTranslations = computed(() => ({
        descriptionLeftColumn: t("page.block.fields.description_leftColumn"),
        descriptionTopCenterColumn: t("page.block.fields.description_topCenterColumn"),
        descriptionRightColumn: t("page.block.fields.description_rightColumn"),
        imageTopLeftCard: t("page.block.fields.image_topLeftCard"),
        imageTopRightCard: t("page.block.fields.image_topRightCard"),
        imageBottomLeftCard: t("page.block.fields.image_bottomLeftCard"),
        imageBottomCenterCard: t("page.block.fields.image_bottomCenterCard"),
        imageBottomRightCard: t("page.block.fields.image_bottomRightCard"),
        imageTopCenterColumn: t("page.block.fields.image_topCenterColumn"),
        imageBottomCenterColumn: t("page.block.fields.image_bottomCenterColumn"),
        imageRightColumn: t("page.block.fields.image_rightColumn"),
        imageLeftColumn: t("page.block.fields.image_leftColumn"),
        titleTopLeftCard: t("page.block.fields.title_topLeftCard"),
        titleTopRightCard: t("page.block.fields.title_topRightCard"),
        titleBottomLeftCard: t("page.block.fields.title_bottomLeftCard"),
        titleBottomCenterCard: t("page.block.fields.title_bottomCenterCard"),
        titleBottomRightCard: t("page.block.fields.title_bottomRightCard"),
        titleBottomCenterColumn: t("page.block.fields.title_bottomCenterColumn"),
        titleLeftColumn: t("page.block.fields.title_leftColumn"),
        titleTopCenterColumn: t("page.block.fields.title_topCenterColumn"),
        titleRightColumn: t("page.block.fields.title_rightColumn"),
        descriptionTopLeftCard: t("page.block.fields.description_topLeftCard"),
        descriptionTopRightCard: t("page.block.fields.description_topRightCard"),
        descriptionBottomLeftCard: t("page.block.fields.description_bottomLeftCard"),
        descriptionBottomCenterCard: t("page.block.fields.description_bottomCenterCard"),
        descriptionBottomRightCard: t("page.block.fields.description_bottomRightCard"),
        tagTopLeftCard: t("page.block.fields.tag_topLeftCard"),
        tagTopRightCard: t("page.block.fields.tag_topRightCard"),
        tagBottomLeftCard: t("page.block.fields.tag_bottomLeftCard"),
        tagBottomRightCard: t("page.block.fields.tag_bottomRightCard"),
        tagBottomCenterCard: t("page.block.fields.tag_bottomCenterCard"),
    }));

    const threeColumnBlock = computed<BlockDef>(() =>
        createBlock<ThreeColumnProps>({
            typeId: "ThreeColumn",
            type: "bentoGrid",
            name: t("page.blocks.bento.threeColumn"),
            component: ThreeColumn,
        })
            .withField("tag", textField(translations.value.tag, "Deploy faster"))
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Everything you need to deploy your app",
                    "h2",
                    { text: translations.value.text, tag: translations.value.tag }
                )
            )
            .withField(
                "leftColumnTitle",
                textField(
                    bentoTranslations.value.titleLeftColumn,
                    "Mobile friendly",
                    translations.value.text
                )
            )
            .withField(
                "leftColumnDescription",
                htmlField(
                    bentoTranslations.value.descriptionLeftColumn,
                    "Responsive design that adapts to any device for a consistent experience across platforms.",
                    translations.value.description
                )
            )
            .withField(
                "leftColumnImage",
                descriptiveImageField(
                    bentoTranslations.value.imageLeftColumn,
                    {
                        src: getStockPhotoUrl("communities", "people-using-smartphones"),
                        alt: "Mobile friendly interface",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )
            .withField(
                "topCenterColumnTitle",
                textField(
                    bentoTranslations.value.titleTopCenterColumn,
                    "Performance",
                    translations.value.text
                )
            )
            .withField(
                "topCenterColumnDescription",
                htmlField(
                    bentoTranslations.value.descriptionTopCenterColumn,
                    "Optimized for speed with lightning-fast load times for exceptional user experiences.",
                    translations.value.description
                )
            )
            .withField(
                "topCenterColumnImage",
                descriptiveImageField(
                    bentoTranslations.value.imageTopCenterColumn,
                    {
                        src: getStockPhotoUrl(
                            "licences-saas",
                            "cloud-system-tablet-background-smart-technology-remixed-media"
                        ),
                        alt: "Performance metrics",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )
            .withField(
                "bottomCenterColumnTitle",
                textField(
                    bentoTranslations.value.titleBottomCenterColumn,
                    "Security",
                    translations.value.text
                )
            )
            .withField(
                "bottomCenterColumnDescription",
                htmlField(
                    t("page.block.fields.description_bottomCenterColumn"),
                    "Enterprise-grade protection with advanced encryption and industry compliance.",
                    translations.value.description
                )
            )
            .withField(
                "bottomCenterColumnImage",
                descriptiveImageField(
                    bentoTranslations.value.imageBottomCenterColumn,
                    {
                        src: getStockPhotoUrl(
                            "licences-saas",
                            "computer-engineer-typing-keyboard-writing-code-build-firewalls"
                        ),
                        alt: "Security features",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )
            .withField(
                "rightColumnTitle",
                textField(
                    bentoTranslations.value.titleRightColumn,
                    "Powerful APIs",
                    translations.value.text
                )
            )
            .withField(
                "rightColumnDescription",
                htmlField(
                    bentoTranslations.value.descriptionRightColumn,
                    "Flexible integration options with comprehensive documentation for simple connectivity.",
                    translations.value.description
                )
            )
            .withField(
                "rightColumnImage",
                descriptiveImageField(
                    bentoTranslations.value.imageRightColumn,
                    {
                        src: getStockPhotoUrl("licences-saas", "coding-man"),
                        alt: "API code example",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
                colorDefaults.value.cardBackground,
                colorDefaults.value.cardText,
            ])
            .build()
    );

    const twoRowBentoBlock = computed<BlockDef>(() =>
        createBlock<TwoRowBentoGridProps>({
            typeId: "TwoRowBentoGrid",
            type: "bentoGrid",
            name: t("page.blocks.bento.twoRow"),
            component: TwoRowBentoGrid,
        })
            // Header fields
            .withField(
                "tag",
                textField(translations.value.tag, "Features", translations.value.text)
            )
            .withField(
                "title",
                headingField(translations.value.title, "Everything you need in one place", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )

            // Top Left Card
            .withField(
                "topLeftCardTag",
                textField(
                    bentoTranslations.value.tagTopLeftCard,
                    "Analytics",
                    translations.value.text
                )
            )
            .withField(
                "topLeftCardTitle",
                headingField(bentoTranslations.value.titleTopLeftCard, "Data Insights", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "topLeftCardDescription",
                htmlField(
                    bentoTranslations.value.descriptionTopLeftCard,
                    "Powerful analytics tools to help you make data-driven decisions and understand your business performance better.",
                    translations.value.description
                )
            )
            .withField(
                "topLeftCardImage",
                descriptiveImageField(
                    bentoTranslations.value.imageTopLeftCard,
                    {
                        src: getStockPhotoUrl(
                            "licences-saas",
                            "female-employee-reviewing-company-data-work-new-strategy"
                        ),
                        alt: "Analytics dashboard",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )

            // Top Right Card
            .withField(
                "topRightCardTag",
                textField(
                    bentoTranslations.value.tagTopRightCard,
                    "Collaboration",
                    translations.value.text
                )
            )
            .withField(
                "topRightCardTitle",
                headingField(
                    bentoTranslations.value.titleTopRightCard,
                    "Team Collaboration",
                    "h3",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "topRightCardDescription",
                htmlField(
                    bentoTranslations.value.descriptionTopRightCard,
                    "Seamless teamwork with real-time collaboration tools and communication features.",
                    translations.value.description
                )
            )
            .withField(
                "topRightCardImage",
                descriptiveImageField(
                    bentoTranslations.value.imageTopRightCard,
                    {
                        src: getStockPhotoUrl(
                            "webinars",
                            "team-meeting-online-conference-call-laptop"
                        ),
                        alt: "Team collaboration",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )

            // Bottom Left Card
            .withField(
                "bottomLeftCardTag",
                textField(
                    bentoTranslations.value.tagBottomLeftCard,
                    "Learning",
                    translations.value.text
                )
            )
            .withField(
                "bottomLeftCardTitle",
                headingField(bentoTranslations.value.titleBottomLeftCard, "Online Learning", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "bottomLeftCardDescription",
                htmlField(
                    bentoTranslations.value.descriptionBottomLeftCard,
                    "Comprehensive training programs and educational resources for continuous growth.",
                    translations.value.description
                )
            )
            .withField(
                "bottomLeftCardImage",
                descriptiveImageField(
                    bentoTranslations.value.imageBottomLeftCard,
                    {
                        src: getStockPhotoUrl(
                            "courses",
                            "cheerful-young-woman-glasses-front-laptop"
                        ),
                        alt: "Online learning platform",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )

            // Bottom Center Card
            .withField(
                "bottomCenterCardTag",
                textField(
                    bentoTranslations.value.tagBottomCenterCard,
                    "Resources",
                    translations.value.text
                )
            )
            .withField(
                "bottomCenterCardTitle",
                headingField(
                    bentoTranslations.value.titleBottomCenterCard,
                    "Digital Resources",
                    "h3",
                    { text: translations.value.text, tag: translations.value.tag }
                )
            )
            .withField(
                "bottomCenterCardDescription",
                htmlField(
                    bentoTranslations.value.descriptionBottomCenterCard,
                    "Access to digital books, guides, and educational materials for enhanced learning.",
                    translations.value.description
                )
            )
            .withField(
                "bottomCenterCardImage",
                descriptiveImageField(
                    bentoTranslations.value.imageBottomCenterCard,
                    {
                        src: getStockPhotoUrl("e-books", "laptop-books-arrangement"),
                        alt: "Digital resources",
                    },
                    { src: translations.value.src, alt: translations.value.alt }
                )
            )

            // Bottom Right Card
            .withField(
                "bottomRightCardTag",
                textField(
                    bentoTranslations.value.tagBottomRightCard,
                    "Events",
                    translations.value.text
                )
            )
            .withField(
                "bottomRightCardTitle",
                headingField(
                    bentoTranslations.value.titleBottomRightCard,
                    "Networking Events",
                    "h3",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "bottomRightCardDescription",
                htmlField(
                    bentoTranslations.value.descriptionBottomRightCard,
                    "Connect with professionals through exclusive networking events and conferences.",
                    translations.value.description
                )
            )
            .withField(
                "bottomRightCardImage",
                descriptiveImageField(
                    bentoTranslations.value.imageBottomRightCard,
                    {
                        src: getStockPhotoUrl(
                            "events-and-tickets",
                            "people-taking-part-business-event"
                        ),
                        alt: "Networking events",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [threeColumnBlock.value, twoRowBentoBlock.value]),
    };
}
